import { useCallback, useEffect, useRef, useState } from "react";

import Message from "./components/Message";
import type { ChatMessage, ToolActivity } from "./components/Message";
import SignIn from "./components/SignIn";
import { AGENT_LABELS, invokeAgent, loadConfig, newSessionId } from "./lib/agui";
import type { AgentName, AppConfig } from "./lib/agui";
import { completeHostedUiSignIn, currentIdentity, signOut } from "./lib/auth";
import type { Identity } from "./lib/auth";
import {
  deleteConversation,
  loadConversations,
  titleFrom,
  upsertConversation,
} from "./lib/history";
import type { Conversation } from "./lib/history";

/**
 * Starter prompts per agent. Each maps to tools the agent actually has — asking
 * about something unsupported makes the demo look broken rather than bounded.
 */
const SUGGESTIONS: Record<AgentName, string[]> = {
  study: [
    "Am I on track to graduate?",
    "Why can't I enrol in COMP2420?",
    "Do I owe any fees, and does that block re-enrolling?",
    "What does the policy say about special consideration?",
  ],
  student_success: [
    // Cohort-wide first: the breakdown is what finds the cell worth caring about,
    // and the rest of the list narrows from there to a student and an intervention.
    "Which campus and study mode is struggling most?",
    "Which students in BINFT are most at risk?",
    "Walk me through u7798221's history before I meet them",
    "Draft an advising note about a study-load conversation",
    "What does the policy say about progression review?",
  ],
};

const AGENT_BLURBS: Record<AgentName, string> = {
  study:
    "Ask about your enrolment, results, fees, or what's blocking your progress.",
  student_success:
    "Ask about a cohort breakdown, who is at risk, a student's history, or drafting an intervention.",
};

/**
 * Which agent a sign-in lands on.
 *
 * Staff open on the Student Success Assistant: it is the agent their role exists
 * for, and dropping an advisor into Study Guide shows them a student's view of the
 * product first. Read off the end of `agents`, which auth.ts orders least-capable
 * first (mirroring entitled_agents in agents/registry.py), so this stays a
 * *presentation* default derived from entitlement rather than a second place that
 * decides who counts as staff.
 */
function landingAgent(identity: Identity): AgentName {
  return identity.agents[identity.agents.length - 1] ?? "study";
}

export default function App() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [booting, setBooting] = useState(true);
  const [bootError, setBootError] = useState<string | null>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [agent, setAgent] = useState<AgentName>("study");
  const sessionId = useRef(newSessionId());
  const abort = useRef<AbortController | null>(null);
  const scrollAnchor = useRef<HTMLDivElement>(null);

  // Saved conversations for the signed-in user. `convoId` is the active one; it is
  // a ref because `send` mutates it mid-turn and every change is already paired
  // with a state update that re-renders (so the sidebar highlight stays correct).
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const convoId = useRef<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const loaded = await loadConfig();
        setConfig(loaded);
        // If we arrived back from Entra's hosted UI (?code=...), finish that
        // exchange; otherwise restore any existing session. completeHostedUiSignIn
        // returns null when there is no code, so this is safe to call every boot.
        const federated = await completeHostedUiSignIn(loaded);
        setIdentity(federated ?? (await currentIdentity(loaded)));
      } catch (err) {
        setBootError(err instanceof Error ? err.message : "Failed to start.");
      } finally {
        setBooting(false);
      }
    })();
  }, []);

  useEffect(() => {
    scrollAnchor.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Load this user's saved conversations once we know who they are.
  useEffect(() => {
    setConversations(identity ? loadConversations(identity.email) : []);
  }, [identity]);

  // Land on the agent that suits whoever just signed in. `identity` changes only at
  // boot, sign-in and sign-out — never while a conversation is open — so this cannot
  // pull the picker out from under openConversation(). setAgent directly rather than
  // switchAgent(): there is no transcript to clear at sign-in, and switchAgent would
  // mint a second session id for nothing.
  useEffect(() => {
    if (identity) setAgent(landingAgent(identity));
  }, [identity]);

  // Persist the active conversation when a turn settles. Keyed on `streaming` so it
  // saves once when a turn starts (the chat appears in the sidebar immediately) and
  // once when it finishes with the full answer — not on every streamed token.
  useEffect(() => {
    if (!identity || messages.length === 0) return;
    const id = convoId.current ?? crypto.randomUUID();
    convoId.current = id;
    const now = Date.now();
    setConversations(
      upsertConversation(identity.email, {
        id,
        agent,
        title: titleFrom(messages),
        sessionId: sessionId.current,
        messages,
        createdAt: now,
        updatedAt: now,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streaming]);

  const send = useCallback(
    async (prompt: string) => {
      if (!config || !identity || streaming || !prompt.trim()) return;

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        text: prompt,
        tools: [],
      };
      const assistantId = crypto.randomUUID();
      const assistant: ChatMessage = {
        id: assistantId,
        role: "assistant",
        text: "",
        tools: [],
      };

      setMessages((prev) => [...prev, userMessage, assistant]);
      setInput("");
      setStreaming(true);

      const patch = (fn: (m: ChatMessage) => ChatMessage) =>
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? fn(m) : m)));

      abort.current = new AbortController();

      try {
        for await (const event of invokeAgent(
          config,
          agent,
          identity.accessToken,
          sessionId.current,
          prompt,
          abort.current.signal,
        )) {
          switch (event.type) {
            case "TEXT_MESSAGE_CONTENT":
              patch((m) => ({ ...m, text: m.text + event.delta }));
              break;

            case "TOOL_CALL_START":
              patch((m) => ({
                ...m,
                tools: [
                  ...m.tools,
                  { id: event.toolCallId, name: event.toolCallName } as ToolActivity,
                ],
              }));
              break;

            case "TOOL_CALL_END":
            case "TOOL_CALL_RESULT":
              patch((m) => ({
                ...m,
                tools: m.tools.map((t) =>
                  t.id === event.toolCallId ? { ...t, finished: true } : t,
                ),
                // The agent reports a denied call as an error result rather than
                // failing the turn; surface it so the boundary is visible.
                blocked:
                  event.type === "TOOL_CALL_RESULT" &&
                  typeof event.content === "string" &&
                  event.content.includes("Blocked")
                    ? [...(m.blocked ?? []), event.content]
                    : m.blocked,
              }));
              break;

            case "RUN_ERROR":
              patch((m) => ({ ...m, error: event.message }));
              break;

            default:
              break;
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Something went wrong.";
        if (message.includes("session expired")) {
          setIdentity(null);
        }
        patch((m) => ({ ...m, error: message }));
      } finally {
        // Any tool still shown as running has finished with the stream.
        patch((m) => ({ ...m, tools: m.tools.map((t) => ({ ...t, finished: true })) }));
        setStreaming(false);
        abort.current = null;
      }
    },
    [agent, config, identity, streaming],
  );

  if (booting) {
    return (
      <div className="min-h-screen grid place-items-center bg-sand">
        <div className="flex flex-col items-center gap-3">
          <img src="/logo.png" alt="" width={44} height={44} />
          <p className="font-display text-sm font-semibold text-ink-muted">
            Sundial University
          </p>
        </div>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="min-h-screen grid place-items-center bg-sand px-4">
        <p className="max-w-md text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-4 py-3">
          {bootError}
        </p>
      </div>
    );
  }

  if (!config) return null;
  if (!identity) return <SignIn config={config} onSignedIn={setIdentity} />;

  /**
   * Switching agents starts a fresh session and clears the transcript.
   *
   * Not cosmetic: the two agents have different boundaries, and carrying one's
   * conversation into the other would replay staff-visible content into Study
   * Guide's context. The backend enforces this too (memory.event_session_id keys
   * events per agent), so this keeps the UI honest rather than being the control.
   * Long-term memory is actor-scoped and deliberately survives the switch.
   */
  const switchAgent = (next: AgentName) => {
    if (next === agent) return;
    abort.current?.abort();
    sessionId.current = newSessionId();
    convoId.current = null;
    setMessages([]);
    setInput("");
    setStreaming(false);
    setAgent(next);
  };

  /** Start a blank conversation for the current agent. */
  const newChat = () => {
    abort.current?.abort();
    sessionId.current = newSessionId();
    convoId.current = null;
    setMessages([]);
    setInput("");
    setStreaming(false);
  };

  /** Reopen a saved conversation, resuming its session so memory continues. */
  const openConversation = (c: Conversation) => {
    abort.current?.abort();
    if (c.agent !== agent) setAgent(c.agent);
    sessionId.current = c.sessionId;
    convoId.current = c.id;
    setMessages(c.messages);
    setInput("");
    setStreaming(false);
  };

  const removeConversation = (id: string) => {
    if (!identity) return;
    setConversations(deleteConversation(identity.email, id));
    if (id === convoId.current) newChat();
  };

  // History is scoped per agent: the two agents have different boundaries, so a
  // student's chats and an advisor's chats never share a list.
  const agentConversations = conversations.filter((c) => c.agent === agent);

  return (
    // The shell is a fixed-height row: a history rail on the left, then the chat
    // column (header / scrolling transcript / input) on the right. h-dvh pins it to
    // the viewport so the header and footer stay put; min-h-0 lets the transcript
    // and the history list shrink below their content and scroll on their own.
    <div className="h-dvh flex bg-sand">
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-line bg-white">
        {/* The wordmark, as on sundialuni.com: this is a university service, and the
            rail is the one place on screen that never scrolls away. */}
        {/* h-16 matches the chat header, so the two hairlines line up. */}
        <div className="flex items-center gap-2.5 px-4 h-16 border-b border-line">
          <img src="/logo.png" alt="" width={30} height={30} className="shrink-0" />
          <span className="font-display text-[15px] font-bold tracking-tight text-ink">
            Sundial University
          </span>
        </div>
        <div className="px-3 py-3">
          <button
            onClick={newChat}
            className="w-full rounded-md bg-gold px-3 py-2 text-sm font-semibold text-ink shadow-[0_10px_22px_-16px_rgba(233,168,28,0.9)] hover:bg-gold-deep"
          >
            + New chat
          </button>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto px-2 pb-2 space-y-1">
          {agentConversations.length === 0 ? (
            <p className="px-2 py-2 text-xs text-ink-faint">
              Your conversations will appear here.
            </p>
          ) : (
            agentConversations.map((c) => (
              <div
                key={c.id}
                className={
                  "group flex items-center gap-1 rounded-md px-2 py-1.5 " +
                  (c.id === convoId.current
                    ? "bg-gold-tint border-l-2 border-gold"
                    : "hover:bg-sand")
                }
              >
                <button
                  onClick={() => openConversation(c)}
                  title={c.title}
                  className="flex-1 min-w-0 truncate text-left text-xs text-ink-muted"
                >
                  {c.title}
                </button>
                <button
                  onClick={() => removeConversation(c.id)}
                  aria-label="Delete conversation"
                  className="shrink-0 px-1 text-xs text-ink-faint opacity-0 transition group-hover:opacity-100 hover:text-rose-600"
                >
                  ✕
                </button>
              </div>
            ))
          )}
        </nav>
        <p className="px-4 py-3 border-t border-line text-[11px] leading-snug text-ink-faint">
          A fictional university, for demonstration.
        </p>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
      <header className="border-b border-line bg-white">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            {/* The rail carries the wordmark on desktop; below md it is hidden, so
                the mark comes back here and the app is never unbranded. */}
            <img
              src="/logo.png"
              alt=""
              width={28}
              height={28}
              className="md:hidden shrink-0"
            />
            <div className="min-w-0">
              <h1 className="font-display text-sm font-bold tracking-tight text-ink truncate">
                {AGENT_LABELS[agent]}
              </h1>
              <p className="text-xs text-ink-muted truncate" title={identity.email}>
                {identity.name ?? identity.email}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Only offered when the token carries a staff group. Entitlement is
                re-resolved server-side per request, so this picker routes a
                request; it does not grant anything. */}
            {identity.agents.length > 1 && (
              <div
                className="flex rounded-md border border-line overflow-hidden"
                role="group"
                aria-label="Choose an assistant"
              >
                {identity.agents.map((name) => (
                  <button
                    key={name}
                    onClick={() => switchAgent(name)}
                    aria-pressed={name === agent}
                    className={
                      name === agent
                        ? "text-xs font-semibold px-2.5 py-1 bg-gold text-ink"
                        : "text-xs px-2.5 py-1 text-ink-muted hover:bg-sand"
                    }
                  >
                    {name === "study" ? "Study" : "Student Success"}
                  </button>
                ))}
              </div>
            )}
            <span
              className={
                identity.isStaff
                  ? "text-xs font-semibold px-2 py-1 rounded-full bg-ink text-white"
                  : "text-xs font-semibold px-2 py-1 rounded-full bg-gold-tint text-gold-ink border border-gold/40"
              }
              title="Determined by your account's group membership"
            >
              {identity.isStaff ? "Staff" : "Student"}
            </span>
            <button
              onClick={() => {
                signOut(config);
                setIdentity(null);
                setMessages([]);
              }}
              className="text-xs text-ink-muted hover:text-ink"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-4 pt-2">
              <div>
                {/* The site's eyebrow-then-heading rhythm, so an empty chat still
                    looks like a page of Sundial's rather than a bare input. The
                    eyebrow names the assistant and the heading greets, so the two
                    don't just repeat the header's label back at you. */}
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-gold-ink">
                  {AGENT_LABELS[agent]}
                </p>
                <h2 className="font-display text-2xl font-bold tracking-tight text-ink mt-2">
                  {identity.name
                    ? `How can I help, ${identity.name.split(" ")[0]}?`
                    : "How can I help?"}
                </h2>
                <p className="text-sm text-ink-muted mt-2">{AGENT_BLURBS[agent]}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS[agent].map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-xs text-ink-muted bg-white border border-line rounded-full px-3 py-1.5 transition hover:border-gold hover:text-ink"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <Message
              key={m.id}
              message={m}
              // The turn in flight is always the last message — the empty
              // assistant bubble `send` appended. Marking it pending is what
              // puts a thinking indicator on screen during the seconds before
              // the first token, when there is otherwise nothing to see.
              pending={streaming && i === messages.length - 1}
            />
          ))}
          <div ref={scrollAnchor} />
        </div>
      </main>

      <footer className="border-t border-line bg-white">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send(input);
          }}
          className="max-w-3xl mx-auto px-4 py-3 flex gap-2"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={streaming ? "Thinking…" : "Ask a question"}
            disabled={streaming}
            className="flex-1 rounded-full border border-line bg-white px-4 py-2 text-sm text-ink placeholder:text-ink-faint focus:border-gold focus:outline-none disabled:bg-sand"
          />
          {streaming ? (
            <button
              type="button"
              onClick={() => abort.current?.abort()}
              className="rounded-full border border-line px-4 py-2 text-sm text-ink-muted hover:border-ink hover:text-ink"
            >
              Stop
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim()}
              className="rounded-full bg-gold text-ink px-5 py-2 text-sm font-semibold hover:bg-gold-deep disabled:opacity-40"
            >
              Send
            </button>
          )}
        </form>
      </footer>
      </div>
    </div>
  );
}
